/**
 * Persistence for the pre-signup diagnostic.
 *
 * Kept out of React on purpose so the rules that actually matter --
 * resume, expiry, versioning, idempotency -- are unit-testable without
 * mounting a component.
 *
 * WHY PERSISTENCE IS NOT OPTIONAL
 * The flow takes six to eight minutes and the traffic is overwhelmingly
 * mobile. A phone WILL background the tab mid-run. Losing progress there
 * loses the user, so state is written after every single answer.
 *
 * WHY THE SESSION ID EXISTS
 * Signup bounces through an OAuth redirect, users double-tap buttons, and
 * networks retry. `diagnosticSessionId` is the backend idempotency key:
 * the same completed diagnostic replayed any number of times must produce
 * one set of rows, never six.
 */

import {
  QUESTION_SET_VERSION,
  type DiagnosticAnswer,
} from "./diagnosticQuestions";

export const DIAGNOSTIC_STORE_KEY = "maren_diagnostic_v1";

/** Progress older than this is stale enough that resuming would confuse
 *  more than it helps. Long enough to survive a night's sleep. */
export const DIAGNOSTIC_TTL_MS = 24 * 60 * 60 * 1000;

export interface DiagnosticSession {
  diagnosticSessionId: string;
  questionSetVersion: string;
  startedAt: string;
  updatedAt: string;
  /** Index of the question the user is on. */
  cursor: number;
  answers: DiagnosticAnswer[];
  /** Set once the answers have been accepted by the backend, so a resume
   *  never re-submits a diagnostic that already landed. */
  flushedAt?: string;
}

function newId(): string {
  // crypto.randomUUID is unavailable on older Safari and in some
  // embedded webviews, which are exactly the browsers this traffic uses.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createSession(now: Date = new Date()): DiagnosticSession {
  const iso = now.toISOString();
  return {
    diagnosticSessionId: newId(),
    questionSetVersion: QUESTION_SET_VERSION,
    startedAt: iso,
    updatedAt: iso,
    cursor: 0,
    answers: [],
  };
}

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;

function store(explicit?: Storage): Storage | null {
  if (explicit) return explicit;
  try {
    // Private-mode Safari throws on access, not just on write.
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Never let a storage failure break the quiz. Losing resume is a
 *  degraded experience; throwing is a broken one. */
export function saveSession(s: DiagnosticSession, storage?: Storage): void {
  const st = store(storage);
  if (!st) return;
  try {
    st.setItem(
      DIAGNOSTIC_STORE_KEY,
      JSON.stringify({ ...s, updatedAt: new Date().toISOString() }),
    );
  } catch {
    /* quota exceeded or private mode: proceed without resume */
  }
}

export function clearSession(storage?: Storage): void {
  const st = store(storage);
  if (!st) return;
  try {
    st.removeItem(DIAGNOSTIC_STORE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Returns a resumable session, or null. Discards anything expired,
 * already flushed, or written against a different question set -- a
 * half-finished run against an old set would mix questions the result
 * screen can no longer explain.
 */
export function loadSession(
  storage?: Storage,
  now: Date = new Date(),
): DiagnosticSession | null {
  const st = store(storage);
  if (!st) return null;
  let raw: string | null = null;
  try {
    raw = st.getItem(DIAGNOSTIC_STORE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;

  const s = parsed as Partial<DiagnosticSession>;
  if (
    typeof s.diagnosticSessionId !== "string" ||
    typeof s.updatedAt !== "string" ||
    !Array.isArray(s.answers)
  ) {
    return null;
  }
  if (s.questionSetVersion !== QUESTION_SET_VERSION) return null;
  if (s.flushedAt) return null;

  const age = now.getTime() - new Date(s.updatedAt).getTime();
  if (!Number.isFinite(age) || age > DIAGNOSTIC_TTL_MS || age < 0) return null;

  return {
    diagnosticSessionId: s.diagnosticSessionId,
    questionSetVersion: s.questionSetVersion,
    startedAt: s.startedAt ?? s.updatedAt,
    updatedAt: s.updatedAt,
    cursor: typeof s.cursor === "number" ? s.cursor : s.answers.length,
    answers: s.answers as DiagnosticAnswer[],
  };
}

export function recordAnswer(
  s: DiagnosticSession,
  answer: DiagnosticAnswer,
): DiagnosticSession {
  // Re-answering the same question replaces rather than appends, so a
  // back-navigation cannot produce two rows for one question.
  const answers = s.answers.filter((a) => a.id !== answer.id).concat(answer);
  return { ...s, answers, cursor: answers.length, updatedAt: new Date().toISOString() };
}

export function markFlushed(
  s: DiagnosticSession,
  now: Date = new Date(),
): DiagnosticSession {
  return { ...s, flushedAt: now.toISOString() };
}

export function isComplete(s: DiagnosticSession, total: number): boolean {
  return s.answers.length >= total;
}
